/**
 * WidgetWrapper — card wrapper for each dashboard widget.
 * Provides header with title, drag handle, and action buttons.
 */

import { memo } from 'react'
import { GripVertical, Settings, Trash2 } from 'lucide-react'
import { Card } from '@/components/ui'
import type { DashboardWidget } from '@/types/dashboard'

interface WidgetWrapperProps {
  widget: DashboardWidget
  isEditMode: boolean
  onConfigure?: (widgetId: string) => void
  onRemove?: (widgetId: string) => void
  children: React.ReactNode
}

export const WidgetWrapper = memo(function WidgetWrapper({
  widget,
  isEditMode,
  onConfigure,
  onRemove,
  children,
}: WidgetWrapperProps) {
  return (
    <Card className={`h-full flex flex-col gap-0 overflow-visible transition-shadow ${isEditMode ? 'ring-1 ring-primary/30 shadow-md' : ''}`}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/60 shrink-0">
        {isEditMode && (
          <div className="widget-drag-handle cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground -ml-1">
            <GripVertical className="w-4 h-4" />
          </div>
        )}
        <h3 className="text-sm font-semibold text-foreground truncate flex-1 min-w-0">
          {widget.title}
        </h3>
        {isEditMode && (
          <div className="flex items-center gap-0.5 shrink-0">
            {onConfigure && (
              <button
                onClick={() => onConfigure(widget.id)}
                className="p-1 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 transition-colors"
                title="Configure widget"
              >
                <Settings className="w-3.5 h-3.5" />
              </button>
            )}
            {onRemove && (
              <button
                onClick={() => onRemove(widget.id)}
                className="p-1 rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                title="Remove widget"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
      {/* Content */}
      <div className="flex-1 min-h-0 p-3 overflow-visible">
        {children}
      </div>
    </Card>
  )
})
